import type { OcenaSlowna, WynikOceny } from "./ocenaSlowna.js";

/**
 * Trening ("Cwiczenia"): kontrakt miedzy serwerem a klientem. Wylacznie stale, typy i male czyste funkcje, bez DOM
 * i bez API Node. Opis calosci: docs/trening.md.
 *
 * Uzytkownik cwiczy na symulowanym rozmowcy ("klient"), ktorego gra model tekstowy. Cwiczenie to staly tekst z
 * metodyki: wersja TRENINGOWA cwiczenia z soczewki (methodology/trening/cwiczenia.json, wpis wskazuje cwiczenie z
 * methodology/lenses/*.json po slugu i numerze), wykonalna w samej rozmowie. Poziom trudnosci (1-5) steruje
 * zachowaniem klienta (methodology/trening/poziomy.json). Po rozmowie model ocenia, w ilu odpowiedziach uzytkownik
 * zrobil to, czego wymaga cwiczenie, a ocena slowna liczy sie w KODZIE (shared/ocenaSlowna.ts), nie u modelu.
 */

export const POZIOMY_TRENINGU = [1, 2, 3, 4, 5] as const;
export type PoziomTreningu = (typeof POZIOMY_TRENINGU)[number];

export function czyPoziomTreningu(wartosc: unknown): wartosc is PoziomTreningu {
  return typeof wartosc === "number" && (POZIOMY_TRENINGU as readonly number[]).includes(wartosc);
}

/** Najdluzsza pojedyncza odpowiedz uzytkownika w znakach. */
export const MAX_ZNAKOW_WIADOMOSCI_TRENINGU = 1500;

/** Najdluzszy opis rozmowcy ("scenariusz") w znakach. */
export const MAX_ZNAKOW_SCENARIUSZA = 300;

/** Ile odpowiedzi uzytkownika zmiesci jeden trening. Po tylu odpowiedziach trzeba zakonczyc (kazda tura to platne wywolanie modelu). */
export const MAX_TUR_UCZNIA = 20;

/** Ile odpowiedzi uzytkownika trzeba, zeby trening dalo sie ocenic. Mniej: "za malo danych", bez wywolania modelu. */
export const MIN_TUR_DO_OCENY = 3;

/**
 * DOMYSLNE minimum odpowiedzi, w ktorych cwiczenie w ogole mialo zastosowanie, zeby wolno bylo wystawic ocene slowna
 * (reszta: "za malo danych"). Wlasciwe minimum ma kazde cwiczenie treningowe (`minTurStosujacych` we wpisie
 * methodology/trening/cwiczenia.json, patrz zakres nizej); ta wartosc obowiazuje tam, gdzie wpisu nie ma: w zapisie
 * oceny bez migawki minimum i gdy wpis znikl z metodyki albo zmienil tresc po zalozeniu treningu.
 */
export const MIN_TUR_STOSUJACYCH_SIE = 3;

/** Zakres `minTurStosujacych` we wpisie cwiczenia treningowego (najmniej i najwiecej odpowiedzi, liczba calkowita). */
export const NAJMNIEJ_MIN_TUR_STOSUJACYCH = 1;
export const NAJWIECEJ_MIN_TUR_STOSUJACYCH = 5;

export function czyMinTurStosujacych(wartosc: unknown): wartosc is number {
  return (
    typeof wartosc === "number" &&
    Number.isInteger(wartosc) &&
    wartosc >= NAJMNIEJ_MIN_TUR_STOSUJACYCH &&
    wartosc <= NAJWIECEJ_MIN_TUR_STOSUJACYCH
  );
}

export const STATUSY_TRENINGU = ["ACTIVE", "FINISHED"] as const;
export type StatusTreningu = (typeof STATUSY_TRENINGU)[number];

export type RolaWiadomosci = "klient" | "uczen";

export interface WiadomoscTreningu {
  rola: RolaWiadomosci;
  tekst: string;
  /** ISO. */
  utworzono: string;
}

/** Jeden poziom trudnosci do pokazania w wyborze. Zachowanie klienta (tresc polecenia dla modelu) NIE idzie do klienta HTTP. */
export interface PoziomTreninguDto {
  poziom: PoziomTreningu;
  /** "Latwy", "Sredni", "Trudny", "Bardzo trudny", "Ekstremalny". */
  nazwa: string;
  /** Jedno zdanie dla uzytkownika: czego sie spodziewac po rozmowcy. */
  opis: string;
}

/**
 * Cwiczenie do treningu: WERSJA TRENINGOWA cwiczenia z metodyki (methodology/trening/cwiczenia.json), wykonalna w
 * symulowanej rozmowie. Katalog pokazuje tylko cwiczenia, ktore taki wpis maja.
 */
export interface CwiczenieKatalogDto {
  lensSlug: string;
  /** Tytul parametru po polsku ("Dopasowanie do rozmowcy"). */
  parametr: string;
  /** Numer cwiczenia w `cwiczenia` tego parametru w soczewce (methodology/lenses/<slug>.json), od 0. */
  indeks: number;
  /** Tresc treningowa cwiczenia (nie tresc z soczewki, ktora opisuje czynnosc na cale spotkanie). */
  tekst: string;
}

export interface KatalogTreninguDto {
  poziomy: PoziomTreninguDto[];
  cwiczenia: CwiczenieKatalogDto[];
}

/** Ocena jednej odpowiedzi uzytkownika. */
export interface OcenaTuryDto {
  /** Numer odpowiedzi uzytkownika w tym treningu, od 1 (nie numer wiadomosci w rozmowie). */
  numer: number;
  /** Czy cwiczenie mialo w tej odpowiedzi zastosowanie (np. "sparafrazuj pytanie" nie dotyczy odpowiedzi bez pytania). */
  stosuje: boolean;
  /** Czy uzytkownik zrobil to, czego wymaga cwiczenie. Sensowne tylko przy `stosuje`. */
  spelnia: boolean;
  /** Doslowny fragment odpowiedzi uzytkownika, ktory model wskazal jako dowod. Tylko ZWERYFIKOWANY w kodzie, inaczej null. */
  fragment: string | null;
  komentarz: string;
}

export interface OcenaTreninguDto {
  tury: OcenaTuryDto[];
  /** Krotkie podsumowanie po polsku. */
  podsumowanie: string;
  /** Jedno konkretne zalecenie na nastepny trening. */
  nastepnyKrok: string;
  /** Ocena slowna liczona w kodzie z `tury` (shared/ocenaSlowna.ts), albo "za malo danych". */
  wynik: WynikOceny;
}

export interface TreningDto {
  id: string;
  status: StatusTreningu;
  lensSlug: string;
  parametr: string;
  /** Numer cwiczenia w `cwiczenia` tego parametru (od 0), jak w CwiczenieKatalogDto.indeks: pozwala zalozyc ten sam trening jeszcze raz. */
  cwiczenieIndeks: number;
  cwiczenieTekst: string;
  poziom: PoziomTreningu;
  poziomNazwa: string;
  /** Opis rozmowcy podany przez uzytkownika przy zakladaniu (albo null). */
  scenariusz: string | null;
  /** Analiza, z ktorej uzytkownik przyszedl na trening (albo null). */
  zrodloAnalizyId: string | null;
  wiadomosci: WiadomoscTreningu[];
  liczbaTurUcznia: number;
  maxTurUcznia: number;
  minTurDoOceny: number;
  /** Tylko dla ZAKONCZONEGO treningu. */
  ocena: OcenaTreninguDto | null;
  /** ISO. */
  utworzono: string;
  zakonczono: string | null;
}

/** Wiersz listy treningow konta. */
export interface TreningListaDto {
  id: string;
  status: StatusTreningu;
  parametr: string;
  cwiczenieTekst: string;
  poziom: PoziomTreningu;
  poziomNazwa: string;
  liczbaTurUcznia: number;
  /** Ocena slowna zakonczonego treningu (null: trwa albo za malo danych). */
  ocena: OcenaSlowna | null;
  utworzono: string;
}

/** POST /api/trening. */
export interface ZalozenieTreninguDto {
  lensSlug: string;
  cwiczenieIndeks: number;
  poziom: PoziomTreningu;
  scenariusz?: string;
  zrodloAnalizyId?: string;
}

/** POST /api/trening/:id/wiadomosc. */
export interface WiadomoscWejsciowaDto {
  tekst: string;
}

/**
 * Kody bledow tras treningu (pole `error` odpowiedzi). Klient mapuje je na zdania, serwer nigdy nie wysyla przez nie
 * nazwy dostawcy ani modelu (bladDlaUzytkownika w services/widokDlaKonta.ts).
 */
export const KODY_BLEDOW_TRENINGU = [
  /** 400: zle pole (poziom, cwiczenie, dlugosc tekstu). Szczegoly w polu `pole`. */
  "zly_parametr",
  /** 404: trening nie istnieje ALBO nalezy do innego konta (te dwa przypadki sa nieodroznialne). */
  "nie_ma_takiego_treningu",
  /** 404: nie ma takiego parametru albo cwiczenia w metodyce. */
  "nie_ma_takiego_cwiczenia",
  /** 404: analiza podana jako `zrodloAnalizyId` nie istnieje albo konto jej nie widzi (ten sam kod co w trasach analiz). */
  "nie_ma_takiej_sesji",
  /** 409: trening jest juz zakonczony. */
  "trening_zakonczony",
  /** 409: osiagnieto MAX_TUR_UCZNIA, trzeba zakonczyc. */
  "limit_tur",
  /** 409: za malo odpowiedzi, zeby zakonczyc z ocena (MIN_TUR_DO_OCENY). */
  "za_malo_tur",
  /** 409: poprzednia odpowiedz tego treningu jeszcze sie liczy. */
  "trwa_odpowiedz",
  /** 429: za duzo jednoczesnych wywolan z treningu (sufit w aplikacji albo drugie zakladanie na tym koncie). Nic nie zapisano, ponowienie ma sens. */
  "zbyt_wiele_zadan",
  /** 502: model nie odpowiedzial albo odpowiedz nie przeszla kontroli. Tekst bez szczegolow dostawcy. */
  "blad_modelu",
  /** 503: model niedostepny (brak klucza). Ogolne zdanie dla konta user. */
  "model_niedostepny",
] as const;
export type KodBleduTreningu = (typeof KODY_BLEDOW_TRENINGU)[number];

/** Zdanie po polsku dla kodu bledu treningu (klient). Kod nieznany: ogolne zdanie. */
export function komunikatBleduTreningu(kod: string): string {
  switch (kod) {
    case "zly_parametr":
      return "Coś jest nie tak z danymi. Sprawdź je i spróbuj jeszcze raz.";
    case "nie_ma_takiego_treningu":
      return "Nie ma takiego treningu.";
    case "nie_ma_takiego_cwiczenia":
      return "Nie ma takiego ćwiczenia.";
    case "nie_ma_takiej_sesji":
      return "Nie ma takiej analizy.";
    case "trening_zakonczony":
      return "Ten trening jest już zakończony.";
    case "limit_tur":
      return "To była ostatnia możliwa odpowiedź w tym treningu. Zakończ go, żeby zobaczyć ocenę.";
    case "za_malo_tur":
      return `Napisz co najmniej ${MIN_TUR_DO_OCENY} odpowiedzi, żeby dało się ocenić trening.`;
    case "trwa_odpowiedz":
      return "Rozmówca jeszcze odpowiada. Poczekaj chwilę.";
    case "zbyt_wiele_zadan":
      return "Serwer obsługuje teraz inne ćwiczenia. Spróbuj jeszcze raz za chwilę.";
    case "model_niedostepny":
      return "Trening jest chwilowo niedostępny. Spróbuj ponownie później albo daj znać administratorowi.";
    case "blad_modelu":
      return "Nie udało się uzyskać odpowiedzi rozmówcy. Spróbuj jeszcze raz.";
    default:
      return "Coś poszło nie tak. Spróbuj jeszcze raz.";
  }
}
